/**
 * =============================================================================
 * Hook: useTenantMembers
 * Versão: 1.0.0 (2026-05-06)
 *
 * Descrição:
 *   Fonte ÚNICA de leitura de membros do tenant ativo no frontend.
 *   Substitui múltiplas chamadas diretas em `supabase.from('tenant_members')`
 *   espalhadas pelo app (CrmUserFilter, UserSelector, SystemUserSelector,
 *   useKanban, RuleActionForm, etc.), eliminando o loop de SELECTs repetidos
 *   visíveis no Network do navegador.
 *
 *   Internamente chama a RPC server-side `get_tenant_members_safe` que:
 *     - Valida acesso via `get_validated_tenant_id`
 *     - Faz JOIN com `profiles` no servidor
 *     - Loga tentativas de bypass em `security_bypass_attempts`
 *
 *   Como todos os consumidores compartilham a mesma queryKey, o React Query
 *   reutiliza o cache entre componentes (1 request por tenant ativo).
 * =============================================================================
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const logger = createLogger('TenantMembers');

/** Item retornado pela RPC `get_tenant_members_safe`. */
export interface TenantMemberRow {
  user_id: string;
  full_name: string | null;
  email: string | null;
  avatar_url: string | null;
  is_owner: boolean;
  is_active: boolean;
  online_status: string | null;
  last_seen_at: string | null;
  available_for_distribution: boolean;
  joined_at: string | null;
}

interface UseTenantMembersOptions {
  /** Se TRUE (default) retorna somente membros ativos do tenant. */
  onlyActive?: boolean;
  /** Habilita/desabilita a query (default TRUE). */
  enabled?: boolean;
}

/**
 * Retorna a lista de membros do tenant ativo (compartilhada via cache).
 */
export function useTenantMembers(options: UseTenantMembersOptions = {}) {
  const { onlyActive = true, enabled = true } = options;
  const { currentTenant } = useAuth();

  return useQuery({
    queryKey: ['tenant-members', currentTenant?.id, onlyActive],
    queryFn: async (): Promise<TenantMemberRow[]> => {
      if (!currentTenant?.id) return [];

      logger.debug('Buscando membros do tenant via RPC', {
        tenantId: currentTenant.id,
        onlyActive,
      });

      // Cast necessário até `types.ts` ser regenerado
      const { data, error } = await (supabase.rpc as any)(
        'get_tenant_members_safe',
        {
          p_tenant_hint: currentTenant.id,
          p_only_active: onlyActive,
        },
      );

      if (error) {
        logger.error('Falha ao buscar membros do tenant', error);
        throw error;
      }

      return (data || []) as TenantMemberRow[];
    },
    enabled: enabled && !!currentTenant?.id,
    // Membros mudam pouco — cache generoso para reaproveitar entre telas
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}
